import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useApp } from "./AppContext";
import { getApiUrl } from "../config/api";
import { actingLocationHeaders } from "../utils/actingLocation";

// Types
export interface VoiceAISettings {
  id?: number;
  salon_id?: number;
  greeting: string;
  transfer_number: string;
  system_prompt_instructions: string;
  voice_id: string;
  voice_provider: string;
  vapi_assistant_id: string;
  is_enabled: boolean;
  use_business_hours_forwarding: boolean;
}

export interface VoiceAIMessage {
  id: number;
  call_id: number | null;
  caller_name: string;
  caller_phone: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface VoiceAIPhoneNumber {
  id: number;
  phone_number: string;
  vapi_phone_id: string;
  label: string;
  status: string;
  created_at: string;
}

export interface VoiceAICall {
  id: number;
  salon_id: number;
  phone_number_id: number;
  vapi_call_id: string;
  direction: string;
  status: string;
  caller_number: string;
  caller_name: string;
  duration_seconds: number | null;
  transcript: string;
  summary: string;
  recording_url: string;
  outcome: string;
  started_at: string;
  ended_at: string;
  created_at: string;
}

export interface VoiceAISchedule {
  id: number;
  salon_id: number;
  name: string;
  timezone: string;
  hours: Record<
    string,
    { enabled: boolean; start_time: string; end_time: string }
  >;
}

export interface AvailableNumber {
  phone_number: string;
  friendly_name: string;
  locality: string;
  region: string;
  country: string;
}

interface Pagination {
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

interface VoiceAIContextType {
  // Settings
  settings: VoiceAISettings | null;
  settingsLoading: boolean;
  fetchSettings: () => Promise<void>;
  updateSettings: (data: Partial<VoiceAISettings>) => Promise<void>;

  // Phone Numbers
  phoneNumbers: VoiceAIPhoneNumber[];
  phoneNumbersLoading: boolean;
  fetchPhoneNumbers: () => Promise<void>;
  addPhoneNumber: (
    phoneNumber: string,
    label: string,
  ) => Promise<VoiceAIPhoneNumber>;
  deletePhoneNumber: (id: number) => Promise<void>;

  // Available Numbers
  availableNumbers: AvailableNumber[];
  availableNumbersLoading: boolean;
  searchAvailableNumbers: (areaCode?: string) => Promise<void>;

  // Calls
  calls: VoiceAICall[];
  callsLoading: boolean;
  callsPagination: Pagination;
  fetchCalls: (page?: number, size?: number) => Promise<void>;
  fetchRecording: (url: string) => Promise<Blob>;

  // Messages
  messages: VoiceAIMessage[];
  messagesLoading: boolean;
  messagesPagination: Pagination;
  unreadMessageCount: number;
  fetchMessages: (page?: number, size?: number) => Promise<void>;
  markMessageRead: (id: number, isRead: boolean) => Promise<void>;

  // Schedules
  schedules: VoiceAISchedule[];
  schedulesLoading: boolean;
  fetchSchedules: () => Promise<void>;
  createSchedule: (data: Partial<VoiceAISchedule>) => Promise<void>;
  updateSchedule: (id: number, data: Partial<VoiceAISchedule>) => Promise<void>;
  deleteSchedule: (id: number) => Promise<void>;
}

const VoiceAIContext = createContext<VoiceAIContextType | undefined>(undefined);

export function VoiceAIProvider({ children }: { children: ReactNode }) {
  const { fetchWithAuth, getIdToken } = useApp();

  // Settings state
  const [settings, setSettings] = useState<VoiceAISettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Phone numbers state
  const [phoneNumbers, setPhoneNumbers] = useState<VoiceAIPhoneNumber[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);

  // Available numbers state
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>(
    [],
  );
  const [availableNumbersLoading, setAvailableNumbersLoading] = useState(false);

  // Messages state
  const [messages, setMessages] = useState<VoiceAIMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [messagesPagination, setMessagesPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    size: 20,
    total_pages: 0,
  });

  // Calls state
  const [calls, setCalls] = useState<VoiceAICall[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsPagination, setCallsPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    size: 20,
    total_pages: 0,
  });

  // Schedules state
  const [schedules, setSchedules] = useState<VoiceAISchedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);

  // Settings
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const url = getApiUrl("VOICE_AI_SETTINGS");
      const data = await fetchWithAuth(url);
      setSettings(data);
    } catch (error: any) {
      console.error("Failed to fetch voice AI settings:", error);
    } finally {
      setSettingsLoading(false);
    }
  }, [fetchWithAuth]);

  const updateSettings = useCallback(
    async (data: Partial<VoiceAISettings>) => {
      const url = getApiUrl("VOICE_AI_SETTINGS");
      const updated = await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      setSettings(updated);
    },
    [fetchWithAuth],
  );

  // Phone Numbers
  const fetchPhoneNumbers = useCallback(async () => {
    setPhoneNumbersLoading(true);
    try {
      const url = getApiUrl("VOICE_AI_PHONE_NUMBERS");
      const data = await fetchWithAuth(url);
      setPhoneNumbers(data.phone_numbers || []);
    } catch (error: any) {
      console.error("Failed to fetch phone numbers:", error);
    } finally {
      setPhoneNumbersLoading(false);
    }
  }, [fetchWithAuth]);

  const addPhoneNumber = useCallback(
    async (phoneNumber: string, label: string) => {
      // The backend handles Twilio purchase + VAPI import + DB write atomically.
      // No third-party credentials are exposed to the browser anymore.
      const url = getApiUrl("VOICE_AI_PHONE_NUMBERS");
      try {
        const result = await fetchWithAuth(url, {
          method: "POST",
          body: JSON.stringify({ phone_number: phoneNumber, label }),
        });
        setPhoneNumbers((prev) => [...prev, result]);
        return result;
      } catch (err: any) {
        // Surface Twilio's "InvalidPhoneNumber" code (21422) as a clear UX
        // message instead of the generic upstream-error envelope.
        const message = String(err?.message || err?.errorBody?.message || err);
        if (message.includes("21422")) {
          throw new Error(
            "Twilio rejected this phone number (21422 — invalid number). Pick another from the list and try again.",
          );
        }
        throw err;
      }
    },
    [fetchWithAuth],
  );

  const deletePhoneNumber = useCallback(
    async (id: number) => {
      // Backend deprovisions VAPI + Twilio + DB together. No browser-side cleanup.
      const url = getApiUrl("VOICE_AI_PHONE_NUMBER_BY_ID", "", {
        phoneNumberId: String(id),
      });
      await fetchWithAuth(url, { method: "DELETE" });
      setPhoneNumbers((prev) => prev.filter((p) => p.id !== id));
    },
    [fetchWithAuth],
  );

  // Available Numbers
  const searchAvailableNumbers = useCallback(
    async (areaCode?: string) => {
      setAvailableNumbersLoading(true);
      try {
        const queryParams = areaCode ? `area_code=${areaCode}` : "";
        const url = getApiUrl("VOICE_AI_AVAILABLE_NUMBERS", queryParams);
        const data = await fetchWithAuth(url);
        setAvailableNumbers(data.available_numbers || []);
      } catch (error: any) {
        console.error("Failed to search available numbers:", error);
      } finally {
        setAvailableNumbersLoading(false);
      }
    },
    [fetchWithAuth],
  );

  // Calls
  const fetchCalls = useCallback(
    async (page: number = 1, size: number = 20) => {
      setCallsLoading(true);
      try {
        const url = getApiUrl("VOICE_AI_CALLS", `page=${page}&size=${size}`);
        const data = await fetchWithAuth(url);
        setCalls(data.calls || []);
        setCallsPagination(
          data.pagination || { total: 0, page, size, total_pages: 0 },
        );
      } catch (error: any) {
        console.error("Failed to fetch calls:", error);
      } finally {
        setCallsLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const fetchRecording = useCallback(
    async (url: string) => {
      const token = await getIdToken();
      if (!token) throw new Error("No authentication token available");

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...actingLocationHeaders(),
        },
      });

      if (!response.ok) throw new Error("Failed to fetch recording");

      return await response.blob();
    },
    [getIdToken],
  );

  // Messages
  const fetchMessages = useCallback(
    async (page: number = 1, size: number = 20) => {
      setMessagesLoading(true);
      try {
        const url = getApiUrl("VOICE_AI_MESSAGES", `page=${page}&size=${size}`);
        const data = await fetchWithAuth(url);
        setMessages(data.messages || []);
        setUnreadMessageCount(data.unread_count || 0);
        setMessagesPagination(
          data.pagination || { total: 0, page, size, total_pages: 0 },
        );
      } catch (error: any) {
        console.error("Failed to fetch messages:", error);
      } finally {
        setMessagesLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const markMessageRead = useCallback(
    async (id: number, isRead: boolean) => {
      const url = getApiUrl("VOICE_AI_MESSAGE_BY_ID", "", {
        messageId: String(id),
      });
      const updated = await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify({ is_read: isRead }),
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, is_read: updated.is_read } : m)),
      );
      setUnreadMessageCount((prev) => Math.max(0, prev + (isRead ? -1 : 1)));
    },
    [fetchWithAuth],
  );

  // Schedules
  const fetchSchedules = useCallback(async () => {
    setSchedulesLoading(true);
    try {
      const url = getApiUrl("VOICE_AI_SCHEDULES");
      const data = await fetchWithAuth(url);
      setSchedules(data.schedules || []);
    } catch (error: any) {
      console.error("Failed to fetch schedules:", error);
    } finally {
      setSchedulesLoading(false);
    }
  }, [fetchWithAuth]);

  const createSchedule = useCallback(
    async (data: Partial<VoiceAISchedule>) => {
      const url = getApiUrl("VOICE_AI_SCHEDULES");
      const result = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(data),
      });
      setSchedules((prev) => [...prev, result]);
    },
    [fetchWithAuth],
  );

  const updateSchedule = useCallback(
    async (id: number, data: Partial<VoiceAISchedule>) => {
      const url = getApiUrl("VOICE_AI_SCHEDULE_BY_ID", "", {
        scheduleId: String(id),
      });
      const result = await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      setSchedules((prev) => prev.map((s) => (s.id === id ? result : s)));
    },
    [fetchWithAuth],
  );

  const deleteSchedule = useCallback(
    async (id: number) => {
      const url = getApiUrl("VOICE_AI_SCHEDULE_BY_ID", "", {
        scheduleId: String(id),
      });
      await fetchWithAuth(url, { method: "DELETE" });
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    },
    [fetchWithAuth],
  );

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value = useMemo(
    () => ({
      settings,
      settingsLoading,
      fetchSettings,
      updateSettings,
      phoneNumbers,
      phoneNumbersLoading,
      fetchPhoneNumbers,
      addPhoneNumber,
      deletePhoneNumber,
      availableNumbers,
      availableNumbersLoading,
      searchAvailableNumbers,
      calls,
      callsLoading,
      callsPagination,
      fetchCalls,
      fetchRecording,
      messages,
      messagesLoading,
      messagesPagination,
      unreadMessageCount,
      fetchMessages,
      markMessageRead,
      schedules,
      schedulesLoading,
      fetchSchedules,
      createSchedule,
      updateSchedule,
      deleteSchedule,
    }),
    [
      settings,
      settingsLoading,
      fetchSettings,
      updateSettings,
      phoneNumbers,
      phoneNumbersLoading,
      fetchPhoneNumbers,
      addPhoneNumber,
      deletePhoneNumber,
      availableNumbers,
      availableNumbersLoading,
      searchAvailableNumbers,
      calls,
      callsLoading,
      callsPagination,
      fetchCalls,
      fetchRecording,
      messages,
      messagesLoading,
      messagesPagination,
      unreadMessageCount,
      fetchMessages,
      markMessageRead,
      schedules,
      schedulesLoading,
      fetchSchedules,
      createSchedule,
      updateSchedule,
      deleteSchedule,
    ],
  );

  return (
    <VoiceAIContext.Provider value={value}>{children}</VoiceAIContext.Provider>
  );
}

export function useVoiceAI() {
  const context = useContext(VoiceAIContext);
  if (!context) {
    throw new Error("useVoiceAI must be used within VoiceAIProvider");
  }
  return context;
}
